import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PlusIcon, MinusIcon, PhoneIcon, MessageSquareIcon } from 'lucide-react';
import { SITE } from '../constants/site';
const FAQS = [{
  q: 'Why do I need a licensed irrigator? Can\u2019t any handyman fix sprinklers?',
  a: `Technically, anyone can replace a sprinkler head. But in Texas, designing, installing, or significantly altering an irrigation system legally requires a state-licensed irrigator. An unlicensed repair can void your home warranty, fail city inspection, and — most critically — create a backflow contamination risk that puts your drinking water at risk. Northface holds License ${SITE.license}, meaning every job we do is legal, code-compliant, and protected. Hiring unlicensed is gambling with your property and your health.`
}, {
  q: 'How do I know if my irrigation system actually has a problem?',
  a: 'Most homeowners don\u2019t know until the damage is done. Common warning signs include: a water bill higher than normal with no explanation, wet or muddy spots in your yard when it hasn\u2019t rained, dry patches even after watering, sprinkler heads that don\u2019t pop up or spray sideways or won\u2019t shut off, and sod or new grass that keeps dying in the same spots. If you notice any of these, call us before it gets worse and more expensive.'
}, {
  q: 'Do you work on commercial properties, not just homes?',
  a: 'Absolutely. Commercial irrigation is one of our specialties. We work with property managers, HOAs, retail centers, office parks, and municipal properties throughout Garland and North Texas. Commercial systems require greater precision, larger zone management, and full documentation — and that\u2019s exactly what we deliver with every commercial job.'
}, {
  q: 'How long does a new irrigation system installation take?',
  a: 'For a standard residential property, most new system installations are completed in 1–2 days. Commercial installs vary depending on square footage and complexity. We\u2019ll give you a firm project timeline before we start — no surprises, no dragging the job out. We respect your time and your property.'
}, {
  q: 'What if something breaks after you fix it?',
  a: 'We\u2019re fully insured and we stand behind our work. If a repair we made or a system we installed fails, we come back and make it right. That\u2019s not a marketing line — it\u2019s backed by our insurance and our reputation in the Garland and North Texas community. We\u2019ve built this business on referrals and repeat customers.'
}, {
  q: 'Can you install sod AND the irrigation system together?',
  a: 'Yes — and honestly, this is the smartest way to do it. Installing sod on top of a broken or poorly designed irrigation system is one of the most common and expensive mistakes homeowners make. We coordinate both so that your sod goes down on a property that\u2019s engineered to keep it alive. One team, one project, one outcome: a lawn that actually grows.'
}, {
  q: 'How does your diagnostic process work?',
  a: 'Call or text us to schedule a professional property walkthrough. We\u2019ll thoroughly assess your current system (or the property if you\u2019re starting fresh), explain exactly what we find, and give you a clear, honest quote for the required work. No pressure, just straight answers from a licensed professional.'
}, {
  q: 'Are you insured? What does that mean for me?',
  a: 'Yes, Northface Irrigation carries full workman\u2019s insurance on every job. What that means for you: you are protected. If a worker is injured on your property, or if something unexpected damages your landscape or structures during the job, you have zero liability. This is especially critical for commercial clients. Never let an uninsured irrigation crew touch your property.'
}];
function FaqItem({
  q,
  a,
  isOpen,
  onToggle
}: {
  q: string;
  a: string;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return <div className="rounded-2xl border border-slate-200 bg-white" data-id="element-18" data-mp="1qvvkqx3w37r7">
      <button onClick={onToggle} className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left" aria-expanded={isOpen} data-id="element-19" data-mp="1aho3oirozccx">
        <span className="font-display text-base font-bold text-slate-900 sm:text-lg" data-id="element-20" data-mp="1ff623f83296o">
          {q}
        </span>
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-nfblue/10 text-nfblue" data-id="element-21" data-mp="16guhbttwe5m5">
          {isOpen ? <MinusIcon className="h-4 w-4" data-id="element-22" data-mp="1u66kabymthg0" /> : <PlusIcon className="h-4 w-4" data-id="element-23" data-mp="1u6yfhlobhd5r" />}
        </span>
      </button>
      <AnimatePresence initial={false} data-id="element-24" data-mp="1y6yv39n5juee">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.25
      }} className="overflow-hidden" data-id="element-25" data-mp="1y9us8vr6ef1s">
            <p className="px-6 pb-6 text-slate-600" data-id="element-26" data-mp="2d3puvk0qn84j">{a}</p>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
export function FaqSection() {
  const [open, setOpen] = useState<number | null>(0);
  return <section id="faq" className="w-full bg-white py-16 sm:py-24" data-id="element-27" data-mp="2r9025gqvcm5z">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8" data-id="element-28" data-mp="2n86dgl5lr3c1">
        <div className="mx-auto max-w-3xl text-center" data-id="element-29" data-mp="310mgvn2f13dh">
          <span className="text-xs font-bold uppercase tracking-wide text-nfgreen-dark" data-id="element-30" data-mp="3104h4ggjo70t">
            FAQ — Objection Handler
          </span>
          <h2 className="mt-3 font-display text-3xl font-extrabold text-slate-900 sm:text-4xl" data-id="element-31" data-mp="28byprauzjjs0">
            Questions? We speak straight.
          </h2>
        </div>

        <div className="mt-10 space-y-3" data-id="element-32" data-mp="24c6g0utuv7l2">
          {FAQS.map((faq, i) => <FaqItem key={i} q={faq.q} a={faq.a} isOpen={open === i} onToggle={() => setOpen(open === i ? null : i)} data-id="element-33" data-mp="2ib64o2hdbmh8" data-mpi={i} />)}
        </div>

        <div className="mt-12 overflow-hidden rounded-2xl bg-nfblue-dark text-center shadow-xl" data-id="element-34" data-mp="2d9qkecn0ljzw">
          <div className="bg-gradient-to-b from-white/10 to-transparent px-6 py-10 sm:p-12" data-id="element-35" data-mp="3tngrztdceqea">
            <p className="font-display text-2xl font-extrabold text-white sm:text-3xl" data-id="element-36" data-mp="3opyq2mxqtbfs">
              Still have questions?
            </p>
            <p className="mx-auto mt-3 max-w-lg text-lg text-slate-300" data-id="element-37" data-mp="4mu1kyx7nwow">
              We answer fast. We speak straight. No runaround. Get the answers you need directly from a licensed expert.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row" data-id="element-38" data-mp="elte4sl3kch6">
              <a href={SITE.phoneHref} className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-nfgreen px-8 py-4 text-base font-bold text-white shadow-lg shadow-nfgreen/30 transition-transform hover:scale-[1.03] hover:bg-nfgreen-dark sm:w-auto" data-id="element-39" data-mp="3d3fg0v8m3hze">
                <PhoneIcon className="h-5 w-5" data-id="element-40" data-mp="3rs3b70ejppot" />
                Call: {SITE.phone}
              </a>
              <a href={SITE.smsHref} className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-white/20 bg-white/5 px-8 py-4 text-base font-bold text-white backdrop-blur transition-colors hover:bg-white/10 sm:w-auto" data-id="element-41" data-mp="2ube9d6plinff">
                <MessageSquareIcon className="h-5 w-5" data-id="element-42" data-mp="2mty86x7cph0h" />
                Text Us
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>;
}